// Review component for displaying soft tag details before saving
import AceEditor from 'react-ace';

import { useAppSelector } from '@/app/hooks';
import { Caption, Subtitle, Title, Button } from '@yaradigitallabs/ahua-react';

import { selectNewSoftTagsLoading } from '../store/createSoftTag.selectors';

import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-monokai';

// Props for Review component
// softTagDetails: object containing all entered soft tag info
// handleConfirmAndSave: callback for saving the soft tag
// handleBack: callback for going back to previous step
type ReviewProps = {
  softTagDetails: any;
  handleConfirmAndSave: () => void;
  handleBack: () => void;
};

// Renders a summary of all soft tag details and navigation buttons
export const Review = ({
  softTagDetails,
  handleConfirmAndSave,
  handleBack,
}: ReviewProps) => {
  const executionCodeLineCount = softTagDetails?.executionCode
    ? softTagDetails.executionCode.split('\n').length
    : 0;
  const editorMaxHeightCondition = executionCodeLineCount >= 5;
  const loading = useAppSelector(selectNewSoftTagsLoading);

  return (
    <div className="w-[50%] m-auto">
      {/* General section: name, description, scope */}
      <div className="w-full mb-4">
        <Title size="n" className="px-4 py-2">
          General
        </Title>
        <div className="flex flex-row justify-between mb-4">
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Soft Tag Name</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.softTagName}
            </Caption>
          </div>
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Description</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.description}
            </Caption>
          </div>
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Scope</Subtitle>
            <Caption className="mt-3" size="n">
              Site level
            </Caption>
          </div>
        </div>
        {/* Production site, area, unit */}
        <div className="flex flex-row justify-between mb-4">
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Production site</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.productionSite}
            </Caption>
          </div>

          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Area</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.area}
            </Caption>
          </div>
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Production unit</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.productionUnit}
            </Caption>
          </div>
        </div>
      </div>
      {/* Properties section: schedule, interval, time series, unit, signal/data type */}
      <div className="w-full mb-4">
        <Title size="n" className="px-4 py-2">
          Properties
        </Title>
        <div className="flex flex-row justify-between mb-4">
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Schedule</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.schedule}
            </Caption>
          </div>
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Scheduling Interval</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.schedulingInterval}
            </Caption>
          </div>
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Signal Name</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.signalName}
            </Caption>
          </div>
        </div>
        <div className="flex flex-row justify-between mb-4">
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Unit</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.unit}
            </Caption>
          </div>

          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Signal type</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.signalType}
            </Caption>
          </div>
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Data type</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.dataType}
            </Caption>
          </div>
          <div className="w-[33%] px-4 py-2">
            <Subtitle size="n">Owner</Subtitle>
            <Caption className="mt-3" size="n">
              {softTagDetails?.owner}
            </Caption>
          </div>
        </div>
        <div className="flex flex-row justify-between mb-4">
          <div className="w-full px-4 py-2">
            <Subtitle size="n">Execution code</Subtitle>
            {softTagDetails?.executionCode ? (
              <AceEditor
                mode="python"
                theme="monokai"
                value={softTagDetails?.executionCode}
                highlightActiveLine={false}
                width="100%"
                name="formula-code"
                editorProps={{ $blockScrolling: true }}
                wrapEnabled={true}
                minLines={5}
                maxLines={editorMaxHeightCondition ? executionCodeLineCount : 5}
                setOptions={{
                  showPrintMargin: false,
                  useWorker: false,
                  wrap: false,
                }}
                readOnly
              />
            ) : (
              <Caption className="mt-3" size="n">
                No execution code added
              </Caption>
            )}
          </div>
        </div>
      </div>
      {/* Navigation buttons */}
      <div className="flex flex-row align-items justify-end gap-3 mt-16">
        <Button
          variant="primary"
          css={{ width: '70px' }}
          size="s"
          onClick={() => handleBack()}
          colorConcept="inverse"
        >
          Back
        </Button>
        <Button
          css={{ width: '220px' }}
          size="s"
          onClick={() => handleConfirmAndSave()}
          loading={loading}
        >
          {loading ? 'Loading...' : 'Confirm and save soft tag'}
        </Button>
      </div>
    </div>
  );
};
